import { useMemo, useContext, useRef, useEffect, useState } from 'react';
import { CalendarContext } from '../CalendarContext';
import { CalendarEventType } from '../constants';
const getEventType = (event) => {
    var _a, _b;
    if (event.isVisit) {
        return CalendarEventType.Visit;
    }
    if ((_a = event.key) === null || _a === void 0 ? void 0 : _a.includes('TimeOff')) {
        return CalendarEventType.Timeoff;
    }
    if ((_b = event.key) === null || _b === void 0 ? void 0 : _b.includes('Holiday')) {
        return CalendarEventType.Holiday;
    }
    return CalendarEventType.Generic_event;
};
export default function useEvents() {
    const { state: { events, statuses, view } } = useContext(CalendarContext);
    const selectedStatusIds = useMemo(() => Object.keys(statuses).filter((key) => statuses[key]), [statuses]);
    const fullCalendarEventsCacheRef = useRef([]);
    const cacheMapRef = useRef(new Map());
    const [rerender, setrerender] = useState(0);
    useEffect(() => {
        let hasChanges = false;
        // Remove any filtered out events
        const newEventKeys = new Set(events.map((x) => x.key));
        const prevCache = fullCalendarEventsCacheRef.current;
        const newCache = prevCache.filter((cachedEvent) => newEventKeys.has(cachedEvent.key));
        if (newCache.length !== prevCache.length) {
            fullCalendarEventsCacheRef.current = newCache;
            // Rebuild map from filtered cache
            const newMap = new Map();
            newCache.forEach((ce) => newMap.set(ce.key, ce));
            cacheMapRef.current = newMap;
            hasChanges = true;
        }
        events.forEach((event) => {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3, _4, _5, _6;
            const { id } = event;
            const cached = cacheMapRef.current.get(event.key);
            const highlightingStatus = selectedStatusIds.includes((_b = (_a = event.status) === null || _a === void 0 ? void 0 : _a.groupStatus) !== null && _b !== void 0 ? _b : '');
            const start = event.start.toDate();
            const end = event.end.toDate();
            const eventType = getEventType(event);
            const normalizedHighlightingStatus = eventType === CalendarEventType.Visit ? highlightingStatus : false;
            const normalizedAllDay = eventType === CalendarEventType.Holiday ? true : event.allDay;
            // For multi-day timed visits, use daySpan to guarantee FullCalendar renders
            // the event across the correct number of days regardless of API end precision.
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const visitDaySpan = 
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            eventType === CalendarEventType.Visit ? event.daySpan : undefined;
            let normalizedEnd;
            if (view === 'dayGridMonth' && visitDaySpan && visitDaySpan > 1) {
                // Month view: extend multi-day visits to midnight boundaries so FullCalendar
                // spans the correct number of days. Week/day views keep raw timed ends.
                normalizedEnd = event.start.startOf('day').add(visitDaySpan, 'day').toDate();
            }
            else {
                normalizedEnd = normalizedAllDay ? event.end.startOf('day').add(1, 'day').toDate() : end;
            }
            // Check if the cached event exists and its computed values are the same
            if (cached &&
                ((_c = cached.highlightingStatus) !== null && _c !== void 0 ? _c : false) === normalizedHighlightingStatus &&
                ((_e = (_d = cached === null || cached === void 0 ? void 0 : cached.data) === null || _d === void 0 ? void 0 : _d.assignee) === null || _e === void 0 ? void 0 : _e.key) === ((_f = event === null || event === void 0 ? void 0 : event.assignee) === null || _f === void 0 ? void 0 : _f.key) &&
                ((_h = (_g = cached === null || cached === void 0 ? void 0 : cached.data) === null || _g === void 0 ? void 0 : _g.status) === null || _h === void 0 ? void 0 : _h.key) === ((_j = event === null || event === void 0 ? void 0 : event.status) === null || _j === void 0 ? void 0 : _j.key) &&
                cached.start.getTime() === start.getTime() &&
                cached.end.getTime() === normalizedEnd.getTime() &&
                cached.type === eventType &&
                cached.allDay === normalizedAllDay &&
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                ((_k = cached.data) === null || _k === void 0 ? void 0 : _k.daySpan) === visitDaySpan &&
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                ((_o = (_m = (_l = cached.data) === null || _l === void 0 ? void 0 : _l.start) === null || _m === void 0 ? void 0 : _m.utcOffset) === null || _o === void 0 ? void 0 : _o.call(_m)) === ((_q = (_p = event.start) === null || _p === void 0 ? void 0 : _p.utcOffset) === null || _q === void 0 ? void 0 : _q.call(_p))) {
                return;
            }
            // Compute a new event object if any of the properties have changed
            let computedEvent;
            if ((event === null || event === void 0 ? void 0 : event.isVisit) === true) {
                computedEvent = {
                    id,
                    key: event.key,
                    type: CalendarEventType.Visit,
                    data: event,
                    start,
                    end: normalizedEnd,
                    backgroundColor: normalizedHighlightingStatus
                        ? `${(_s = (_r = event.status) === null || _r === void 0 ? void 0 : _r.color) !== null && _s !== void 0 ? _s : '#ccc'}31`
                        : (_v = (_u = (_t = event.assignee) === null || _t === void 0 ? void 0 : _t.info) === null || _u === void 0 ? void 0 : _u.color) !== null && _v !== void 0 ? _v : '#fff',
                    borderColor: normalizedHighlightingStatus
                        ? `${(_x = (_w = event.status) === null || _w === void 0 ? void 0 : _w.color) !== null && _x !== void 0 ? _x : '#ccc'}31`
                        : (_0 = (_z = (_y = event.assignee) === null || _y === void 0 ? void 0 : _y.info) === null || _z === void 0 ? void 0 : _z.color) !== null && _0 !== void 0 ? _0 : '#fff',
                    editable: true,
                    highlightingStatus: normalizedHighlightingStatus
                };
            }
            else if ((_1 = event === null || event === void 0 ? void 0 : event.key) === null || _1 === void 0 ? void 0 : _1.includes('TimeOff')) {
                computedEvent = {
                    id,
                    key: event.key,
                    type: CalendarEventType.Timeoff,
                    data: event,
                    start,
                    end,
                    backgroundColor: (_3 = (_2 = event.technician) === null || _2 === void 0 ? void 0 : _2.color) !== null && _3 !== void 0 ? _3 : '#ccc',
                    borderColor: (_5 = (_4 = event.technician) === null || _4 === void 0 ? void 0 : _4.color) !== null && _5 !== void 0 ? _5 : '#ccc',
                    editable: false
                };
            }
            else if ((_6 = event === null || event === void 0 ? void 0 : event.key) === null || _6 === void 0 ? void 0 : _6.includes('Holiday')) {
                computedEvent = {
                    id,
                    key: event.key,
                    type: CalendarEventType.Holiday,
                    data: event,
                    start,
                    end: normalizedEnd,
                    backgroundColor: '#f5f5f888',
                    borderColor: '#f5f5f888',
                    editable: false,
                    allDay: normalizedAllDay,
                    display: 'background'
                };
            }
            else {
                computedEvent = {
                    id,
                    key: event.key,
                    type: CalendarEventType.Generic_event,
                    data: event,
                    start,
                    end: normalizedEnd,
                    backgroundColor: '#EFEFF4',
                    borderColor: '#A7A7BB',
                    editable: true,
                    allDay: normalizedAllDay
                };
            }
            if (cached) {
                // Update in-place in the array
                const index = fullCalendarEventsCacheRef.current.indexOf(cached);
                fullCalendarEventsCacheRef.current[index] = computedEvent;
            }
            else {
                fullCalendarEventsCacheRef.current.push(computedEvent);
            }
            cacheMapRef.current.set(event.key, computedEvent);
            hasChanges = true;
        });
        if (hasChanges) {
            setrerender((prevState) => prevState + 1);
        }
    }, [events, selectedStatusIds, view]);
    return { fullCalendarEventsCacheRef, rerender };
}
